import { useEffect, useState } from "react";
import { PricingAPI } from "./PricingAPI";

/**
 * 日曆查詢的共用資料層。
 *
 * DateWithWeekday 是逐格渲染的，一張表可能有數百格，不可能每格自己打 API。
 * 這裡以「年度」為單位快取，同一年只會發一次請求，同頁多張表與跨頁切換都共用。
 *
 * 假日與補班日分開存 —— 補班日是**上班日**，混進假日就是直接的定價錯誤。
 */

// year -> { holidays: Map(date -> category), workdays: Set(date) }
const cache = {};
// year -> Promise，避免同一年同時發出多次請求
const inflight = {};
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => fn());
}

function loadYear(year) {
  if (cache[year] || inflight[year]) return;

  const from = `${year}-01-01`;
  const to = `${year}-12-31`;

  inflight[year] = Promise.all([
    PricingAPI.holiday.index(from, to),
    PricingAPI.holiday.workdays(from, to),
  ])
    .then(([holidayRes, workdayRes]) => {
      const holidays = new Map();
      (holidayRes.data || []).forEach((row) => holidays.set(row.date, row));
      const workdays = new Set();
      (workdayRes.data || []).forEach((row) => workdays.add(row.date));
      cache[year] = { holidays, workdays };
      notify();
    })
    .catch(() => {
      // 靜默失敗並記成空年度：日期標示是輔助資訊，不該讓整張報表跳錯誤。
      // 寫進 cache 是為了不再重試 —— 否則每次重繪都會再打一次失敗的請求。
      cache[year] = { holidays: new Map(), workdays: new Set(), failed: true };
      notify();
    })
    .finally(() => {
      delete inflight[year];
    });
}

/**
 * 查一個日期在日曆上的身分。
 *
 * 回傳 `{ category, name, isMakeupWorkday }`。該年度尚未載入或載入失敗時
 * category 為 null —— 呼叫端要能容忍這個狀態，退回只看星期幾的顯示。
 */
export function useCalendarDay(date) {
  const year = typeof date === "string" ? date.slice(0, 4) : null;
  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!year) return undefined;
    const listener = () => forceRender((n) => n + 1);
    listeners.add(listener);
    loadYear(year);
    return () => listeners.delete(listener);
  }, [year]);

  if (!year || !cache[year]) return { category: null, name: null, isMakeupWorkday: false };

  const holiday = cache[year].holidays.get(date);
  return {
    category: holiday ? holiday.category : null,
    name: holiday ? holiday.name : null,
    isMakeupWorkday: cache[year].workdays.has(date),
  };
}

/**
 * 一段期間內的特別假日 (國定假日、補假、小年夜)，回傳 Set of 'YYYY-MM-DD'。
 *
 * 給 class 元件與一次要判斷整段期間的呼叫端用 —— useCalendarDay 是逐格查詢的 hook，
 * 對「未來 90 天有哪些是特別假日」這種問題不適用。共用同一份年度快取，不會多打請求。
 *
 * 載入失敗時回傳空 Set 而非拋出：假日只是加分條件 (影響建議晚數 2 或 3)，
 * 不該讓整張定價清單因此消失。
 */
export function fetchSpecialDates(fromIso, toIso) {
  const years = [];
  for (let y = Number(fromIso.slice(0, 4)); y <= Number(toIso.slice(0, 4)); y += 1) {
    years.push(String(y));
  }
  years.forEach(loadYear);
  return Promise.all(years.map((y) => inflight[y] || Promise.resolve())).then(() => {
    const out = new Set();
    years.forEach((y) => {
      if (!cache[y]) return;
      cache[y].holidays.forEach((row, date) => {
        if (row.category === 'special' && date >= fromIso && date <= toIso) out.add(date);
      });
    });
    return out;
  });
}

/** 測試用：清掉快取，讓每個案例從乾淨狀態開始。 */
export function __resetHolidayCalendar() {
  Object.keys(cache).forEach((k) => delete cache[k]);
  Object.keys(inflight).forEach((k) => delete inflight[k]);
  listeners.clear();
}
